import React, { useCallback, useMemo } from 'react'
import { Form } from 'antd'
import styled from 'styled-components'
import LabelAndDescriptionFieldLayout from 'components/Assessments/components/FieldLayout/LabelAndDescriptionFieldLayout'
import QuestionContainer from 'components/Assessments/components/QuestionContainer'
import RadioField from 'components/Assessments/components/RadioField'
import {
  AnswersLayout,
  BaseQuestionProps,
  QuestionLayout
} from 'components/Assessments/components/types'
import { CodingSelectOption, CodingValue } from './types'
import {
  buildFormRules,
  createSelectValueFromEvent,
  createSelectValueProps,
  extractSecondaryText
} from './utils'

export interface CodingRadioQuestionProps extends BaseQuestionProps {
  options: CodingSelectOption[]
  answersLayout?: AnswersLayout
}

function CodingRadioQuestion({
  field,
  formFieldName,
  layout = QuestionLayout.VERTICAL,
  disabled = false,
  required = false,
  customRules = [],
  options,
  answersLayout = AnswersLayout.COLUMN
}: CodingRadioQuestionProps): JSX.Element {
  const secondaryText = extractSecondaryText(field)
  const allRules = buildFormRules(required, field.required, customRules)

  // Ensure the currently selected value remains displayable even if not in options
  const currentValue = Form.useWatch(formFieldName) as CodingValue | undefined
  const mergedOptions = useMemo(() => {
    if (!currentValue || typeof currentValue !== 'object' || !('code' in currentValue)) {
      return options
    }

    const code = String(currentValue.code)
    const display = currentValue.display || code

    const isInOptions = options.some(
      (option) =>
        option.value.toLowerCase() === code.toLowerCase() ||
        option.code.toLowerCase() === code.toLowerCase()
    )

    if (isInOptions) return options

    return [
      ...options,
      {
        label: display,
        value: code,
        code,
        display
      }
    ]
  }, [options, currentValue])

  const getValueFromEvent = useCallback(
    (e: { target?: { value?: unknown } } | string) => {
      const code = typeof e === 'string' ? e : String(e?.target?.value ?? '')
      return createSelectValueFromEvent(mergedOptions)(code)
    },
    [mergedOptions]
  )
  const getValueProps = useCallback(
    (value) => createSelectValueProps(mergedOptions)(value),
    [mergedOptions]
  )

  return (
    <QuestionContainer linkId={field.linkId}>
      <Container>
        <LabelAndDescriptionFieldLayout
          label={field.text}
          secondaryText={secondaryText}
          layout={layout}
        >
          <Form.Item
            name={formFieldName}
            rules={allRules}
            style={{ margin: 0 }}
            getValueFromEvent={getValueFromEvent}
            getValueProps={getValueProps}
          >
            <RadioField
              id={formFieldName}
              disabled={disabled}
              options={mergedOptions.map((option) => ({
                label: option.label,
                value: option.value
              }))}
              layout={answersLayout}
            />
          </Form.Item>
        </LabelAndDescriptionFieldLayout>
      </Container>
    </QuestionContainer>
  )
}

const Container = styled.div`
  margin: 12px 0;
`

export default CodingRadioQuestion
